import { zodResolver } from '@hookform/resolvers/zod';
import {
  PaymentMethodCancelReq,
  PaymentMethodCancelSchema,
  PaymentMethodCancelUI,
} from '@monetize/types/app';
import React from 'react';
import { useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import { useCancelPaymentMethod } from '../../../api/paymentsService';
import { MButton, MCenterModal, MStack } from '../../../components/Monetize';
import { logger } from '../../../services/logger';
import { PaymentMethodCancelForm } from './PaymentMethodCancelForm';

interface PaymentMethodCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentMethodId: string;
  accountId: string | undefined;
}

export const PaymentMethodCancelModal: React.FC<
  PaymentMethodCancelModalProps
> = ({ isOpen, onClose, paymentMethodId, accountId }) => {
  const { mutateAsync: doCancelPaymentMethod, isPending: isLoading } =
    useCancelPaymentMethod();

  const { control, handleSubmit, reset } = useForm<PaymentMethodCancelUI>({
    resolver: zodResolver(PaymentMethodCancelSchema),
    mode: 'onSubmit',
    defaultValues: { cancellationReason: '' },
  });

  // One instance per table row stays mounted, so a reason typed and then
  // abandoned was still there the next time that row's modal opened.
  const handleClose = () => {
    reset();
    onClose();
  };

  const onCancePaymentMethodSubmit = async (
    formData: PaymentMethodCancelReq,
  ) => {
    try {
      await doCancelPaymentMethod({
        paymentMethodId,
        payload: formData,
        accountId,
      });
      handleClose();
    } catch (error) {
      logger.error('Cancel payment method Error ====>', error);
      handleApiErrorToast(error);
    }
  };

  const onCancelPaymentMethodError = (error: any) => {
    logger.error('Cancel payment method Error ====>', error);
  };

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle="Cancel Payment Method"
      size="sm"
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton onClick={handleClose} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            onClick={handleSubmit(
              onCancePaymentMethodSubmit,
              onCancelPaymentMethodError,
            )}
            isLoading={isLoading}
            variant="delete"
            minW="auto"
          >
            Confirm
          </MButton>
        </MStack>
      )}
    >
      <PaymentMethodCancelForm control={control} />
    </MCenterModal>
  );
};
